import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getMarketplaceRenterProfile,
  updateMarketplaceRenterProfile,
  upgradeMarketplaceAccountToOwner,
} from "../../api/marketplaceAuth";
import { getMyOperatorAffiliations } from "../../api/workspaceSupport";
import {
  getMyProfile,
  removeMyProfilePhoto,
  updateMyProfile,
  uploadMyProfilePhoto,
} from "../../api/profile";
import ImageUploadField from "../../components/ImageUploadField";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/BrandContext";
import { titleCaseComplianceLabel } from "../../constants/workspaceCompliance";
import { canAccessMarketplaceRenterHistory } from "../../utils/marketplaceOperatorAccess";
import { buildHostUrl } from "../../config/hostRouting";
import { formatNANP, isValidNANP, toNANPDigits10 } from "../../utils/phone";
import { buildWorkspaceOperationalBoundary } from "../../utils/workspaceOperationalBoundaries";
import MarketplaceShell from "./MarketplaceShell";

const DEFAULT_MARKETPLACE_FORM = {
  display_name: "",
  business_name: "",
  phone: "",
  bio: "",
  license_type: "",
  instagram_handle: "",
  emergency_contact: "",
  default_workspace_operator_mode: "independent_owner",
};

const DEFAULT_CURRENT_SALON_PROFILE = {
  username: "",
  role: "",
  current_salon_id: null,
  current_salon_name: "",
  current_business_id: null,
  current_business_name: "",
  staff_id: null,
  salon_display_name: "",
  phone: "",
  photo_url: "",
  has_current_salon_profile: false,
  linked_salon_ids: [],
  linked_business_ids: [],
};

function accessRoleTitle(value) {
  return value === "owner"
    ? "Owner"
    : value === "manager"
    ? "Manager"
    : value === "admin"
    ? "Admin"
    : value === "technician"
    ? "Technician"
    : value === "reception"
    ? "Reception"
    : roleLabel(value);
}

function ownershipTitle(value) {
  return value === "salon_owned"
    ? "Legacy salon-owned clients"
    : "Independent operator-owned clients";
}

function roleLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "User";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function messageForError(message) {
  switch (message) {
    case "CURRENT_SALON_PROFILE_REQUIRED":
      return "No current salon profile is linked to this login yet.";
    case "FULL_NAME_REQUIRED":
      return "Full name is required.";
    case "USERNAME_INVALID":
      return "Username can only use letters, numbers, underscores, or hyphens.";
    case "USERNAME_TAKEN":
      return "That username is already in use.";
    case "SALON_DISPLAY_NAME_REQUIRED":
      return "Display name is required for the current salon profile.";
    case "SALON_CONTEXT_REQUIRED":
      return "Select a salon before editing salon-specific profile details.";
    case "INVALID_CURRENT_PASSWORD":
      return "Current password is incorrect.";
    default:
      return message;
  }
}

function normalizeIdList(value) {
  const entries = Array.isArray(value) ? value : [];
  return [...new Set(entries.map((entry) => Number(entry)).filter(Number.isFinite))].sort(
    (a, b) => a - b
  );
}

function normalizeCurrentSalonProfile(payload = {}) {
  const phoneDigits = toNANPDigits10(payload?.phone || "");
  return {
    username: payload?.username || "",
    role: String(payload?.role || "").toLowerCase(),
    current_salon_id: Number(payload?.current_salon_id || 0) || null,
    current_salon_name: payload?.current_salon_name || "",
    current_business_id: Number(payload?.current_business_id || 0) || null,
    current_business_name: payload?.current_business_name || "",
    staff_id: Number(payload?.staff_id || 0) || null,
    salon_display_name: payload?.salon_display_name || "",
    phone: phoneDigits || "",
    photo_url: payload?.photo_url || "",
    has_current_salon_profile: Boolean(payload?.has_current_salon_profile),
    linked_salon_ids: normalizeIdList(payload?.linked_salon_ids),
    linked_business_ids: normalizeIdList(payload?.linked_business_ids),
  };
}

function AccessList({ title, rows, emptyMessage }) {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {title}
      </div>
      {rows?.length ? (
        <div className="mt-4 space-y-3">
          {rows.map((row) => (
            <div
              key={`${title}-${row.salon_id}`}
              className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
            >
              <div className="text-sm font-semibold text-slate-900">{row.salon_name}</div>
              <div className="mt-1 text-sm text-slate-600">
                Role: <span className="font-medium text-slate-900">{accessRoleTitle(row.role)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-sm text-slate-600">{emptyMessage}</p>
      )}
    </div>
  );
}

function BadgeList({ values, emptyMessage }) {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {values.length ? (
        values.map((value) => (
          <span
            key={value}
            className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700"
          >
            {value}
          </span>
        ))
      ) : (
        <span className="text-sm text-slate-400">{emptyMessage}</span>
      )}
    </div>
  );
}

function HeroPill({ children, tone = "default" }) {
  const toneClasses =
    tone === "solid"
      ? "border-rose-200 bg-rose-50 text-rose-600"
      : "border-[#2f3566]/15 bg-white text-[#2f3566]";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-4 py-2 text-sm font-semibold ${toneClasses}`}
    >
      {children}
    </span>
  );
}

function SnapshotItem({ label, value, detail = "" }) {
  return (
    <div>
      <div className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-950">{value}</div>
      {detail ? <div className="mt-1 text-sm text-slate-600">{detail}</div> : null}
    </div>
  );
}

export default function MarketplaceAccountProfilePage() {
  const navigate = useNavigate();
  const runtimeBrand = useBrand();
  const { token, user, login, updateCurrentUser } = useAuth();
  const canViewMarketplaceBookings = canAccessMarketplaceRenterHistory(user);

  const [marketplaceProfile, setMarketplaceProfile] = useState(null);
  const [operatorAffiliations, setOperatorAffiliations] = useState([]);
  const [marketplaceForm, setMarketplaceForm] = useState(DEFAULT_MARKETPLACE_FORM);
  const [currentSalonProfile, setCurrentSalonProfile] = useState(
    DEFAULT_CURRENT_SALON_PROFILE
  );
  const [marketplaceLoading, setMarketplaceLoading] = useState(Boolean(token));
  const [currentSalonLoading, setCurrentSalonLoading] = useState(Boolean(token));
  const [marketplaceSaving, setMarketplaceSaving] = useState(false);
  const [currentSalonSaving, setCurrentSalonSaving] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [marketplaceError, setMarketplaceError] = useState("");
  const [currentSalonError, setCurrentSalonError] = useState("");
  const [marketplaceSuccess, setMarketplaceSuccess] = useState("");
  const [currentSalonSuccess, setCurrentSalonSuccess] = useState("");
  const [upgradeError, setUpgradeError] = useState("");
  const [salonPhoneError, setSalonPhoneError] = useState("");
  const [upgradeForm, setUpgradeForm] = useState({
    salon_name: "",
    city: "",
    province: "",
  });

  useEffect(() => {
    if (!user?.is_super_admin) {
      return;
    }
    navigate("/superadmin", { replace: true });
  }, [navigate, user?.is_super_admin]);

  useEffect(() => {
    if (!token) {
      setMarketplaceLoading(false);
      setCurrentSalonLoading(false);
      setMarketplaceProfile(null);
      setOperatorAffiliations([]);
      setCurrentSalonProfile(DEFAULT_CURRENT_SALON_PROFILE);
      return undefined;
    }
    let active = true;
    setMarketplaceLoading(true);
    setCurrentSalonLoading(true);
    setMarketplaceError("");
    setCurrentSalonError("");

    Promise.allSettled([
      getMarketplaceRenterProfile(token),
      getMyProfile(token),
      getMyOperatorAffiliations(token),
    ])
      .then(([marketplaceResult, currentSalonResult, affiliationResult]) => {
        if (!active) return;

        if (marketplaceResult.status === "fulfilled") {
          const data = marketplaceResult.value;
          setMarketplaceProfile(data);
          setMarketplaceForm({
            display_name: data?.display_name || "",
            business_name: data?.business_name || "",
            phone: data?.phone || "",
            bio: data?.bio || "",
            license_type: data?.license_type || "",
            instagram_handle: data?.instagram_handle || "",
            emergency_contact: data?.emergency_contact || "",
            default_workspace_operator_mode:
              data?.default_workspace_operator_mode || "independent_owner",
          });
          setUpgradeForm((prev) => ({
            salon_name: prev.salon_name || data?.business_name || user?.full_name || "",
            city: prev.city || "",
            province: prev.province || "",
          }));
          setMarketplaceError("");
        } else {
          setMarketplaceError(
            messageForError(
              marketplaceResult.reason?.message ||
                "Unable to load your Ellyanna Boothshare account."
            )
          );
        }

        if (currentSalonResult.status === "fulfilled") {
          setCurrentSalonProfile(normalizeCurrentSalonProfile(currentSalonResult.value));
          setCurrentSalonError("");
        } else {
          setCurrentSalonError(
            messageForError(
              currentSalonResult.reason?.message ||
                "Unable to load your current salon profile."
            )
          );
        }

        if (affiliationResult.status === "fulfilled") {
          setOperatorAffiliations(
            Array.isArray(affiliationResult.value) ? affiliationResult.value : []
          );
        } else {
          setOperatorAffiliations([]);
        }
      })
      .finally(() => {
        if (!active) return;
        setMarketplaceLoading(false);
        setCurrentSalonLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token, user?.full_name]);

  const defaultOwnershipMode = "independent_owned";

  const linkedSalonIds = useMemo(
    () => normalizeIdList(currentSalonProfile.linked_salon_ids),
    [currentSalonProfile.linked_salon_ids]
  );
  const linkedBusinessIds = useMemo(
    () => normalizeIdList(currentSalonProfile.linked_business_ids),
    [currentSalonProfile.linked_business_ids]
  );
  const ownerSalonCount = marketplaceProfile?.owner_salons?.length || 0;
  const technicianSalonCount = marketplaceProfile?.technician_salons?.length || 0;
  const currentRoleLabel = roleLabel(currentSalonProfile.role || user?.role);
  const accountStateLabel = token ? "Signed in" : "Guest";
  const ownershipModeLabel = ownershipTitle(
    marketplaceProfile?.default_client_ownership_mode ||
      user?.default_client_ownership_mode ||
      "independent_owned"
  );
  const heroContextLabel = currentSalonProfile.current_salon_id
    ? "Salon context active"
    : "Renter context";
  const operationalBoundary = useMemo(
    () =>
      marketplaceProfile?.operational_boundary ||
      buildWorkspaceOperationalBoundary({
        occupancyMode:
          marketplaceProfile?.default_workspace_operator_mode ||
          marketplaceForm.default_workspace_operator_mode,
        clientOwnershipMode:
          marketplaceProfile?.default_client_ownership_mode || defaultOwnershipMode,
      }),
    [defaultOwnershipMode, marketplaceForm.default_workspace_operator_mode, marketplaceProfile]
  );

  const applyCurrentSalonResponse = (data) => {
    const normalized = normalizeCurrentSalonProfile(data);
    setCurrentSalonProfile(normalized);
    updateCurrentUser({
      full_name: data?.full_name || user?.full_name || "",
      email: data?.email || user?.email || "",
      photo_url: normalized.photo_url || "",
    });
  };

  const handleSaveMarketplaceIdentity = async (event) => {
    event.preventDefault();
    if (!token) return;
    setMarketplaceSaving(true);
    setMarketplaceError("");
    setMarketplaceSuccess("");
    try {
      const accountData = await updateMyProfile(
        {
          username: currentSalonProfile.username.trim() || null,
        },
        token
      );
      applyCurrentSalonResponse(accountData);
      const data = await updateMarketplaceRenterProfile(marketplaceForm, token);
      setMarketplaceProfile(data);
      setMarketplaceForm({
        display_name: data?.display_name || "",
        business_name: data?.business_name || "",
        phone: data?.phone || "",
        bio: data?.bio || "",
        license_type: data?.license_type || "",
        instagram_handle: data?.instagram_handle || "",
        emergency_contact: data?.emergency_contact || "",
        default_workspace_operator_mode:
          data?.default_workspace_operator_mode || "independent_owner",
      });
      updateCurrentUser({
        renter_profile: data,
        renter_profile_id: data?.id ?? null,
        default_workspace_operator_mode: data?.default_workspace_operator_mode ?? null,
        default_client_ownership_mode: data?.default_client_ownership_mode ?? null,
        has_owner_access: Boolean(data?.has_owner_access),
        has_technician_access: Boolean(data?.has_technician_access),
      });
      setMarketplaceSuccess("Your Ellyanna Boothshare operator profile was saved.");
    } catch (err) {
      setMarketplaceError(
        messageForError(err.message || "Unable to save your marketplace identity.")
      );
    } finally {
      setMarketplaceSaving(false);
    }
  };

  const handleUploadSalonPhoto = async (file) => {
    if (!file || !token) return "";
    const data = await uploadMyProfilePhoto(file, token);
    applyCurrentSalonResponse(data);
    return data?.photo_url || "";
  };

  const handleRemoveSalonPhoto = async () => {
    if (!token) return "";
    const data = await removeMyProfilePhoto(token);
    applyCurrentSalonResponse(data);
    return data?.photo_url || "";
  };

  const handleSaveCurrentSalonProfile = async (event) => {
    event.preventDefault();
    if (!token || !currentSalonProfile.has_current_salon_profile) return;

    const phoneDigits = currentSalonProfile.phone
      ? toNANPDigits10(currentSalonProfile.phone)
      : "";
    if (phoneDigits && !isValidNANP(phoneDigits)) {
      setSalonPhoneError("Phone must be a 10-digit North American number.");
      return;
    }

    setCurrentSalonSaving(true);
    setCurrentSalonError("");
    setCurrentSalonSuccess("");
    setSalonPhoneError("");
    try {
      const payload = {
        salon_display_name: currentSalonProfile.salon_display_name.trim(),
        phone: phoneDigits || null,
      };
      const data = await updateMyProfile(payload, token);
      applyCurrentSalonResponse(data);
      setCurrentSalonSuccess("Your current salon profile was saved.");
    } catch (err) {
      setCurrentSalonError(
        messageForError(err?.message || "Unable to save your current salon profile.")
      );
    } finally {
      setCurrentSalonSaving(false);
    }
  };

  const handleUpgrade = async (event) => {
    event.preventDefault();
    if (!token) return;
    setUpgrading(true);
    setUpgradeError("");
    try {
      const data = await upgradeMarketplaceAccountToOwner(upgradeForm, token);
      if (data?.access_token) {
        login(data.access_token, data);
      }
      const nextUrl = data?.next_url || "/setup";
      window.location.assign(buildHostUrl(runtimeBrand.opsHost, nextUrl));
    } catch (err) {
      setUpgradeError(err.message || "Unable to upgrade this account right now.");
    } finally {
      setUpgrading(false);
    }
  };

  if (user?.is_super_admin) {
    return null;
  }

  return (
    <MarketplaceShell>
      <section className="space-y-6">
        <article className="overflow-hidden rounded-[28px] border border-white/70 bg-white/75 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur">
          <div className="px-7 py-7 text-slate-900 lg:px-9 lg:py-8">
            <div className="grid gap-8 lg:grid-cols-[1.2fr,0.9fr] lg:items-center">
              <div>
                <p className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-rose-600">
                  Ellyanna Boothshare Account
                </p>
                <h1 className="mt-4 max-w-3xl text-4xl font-semibold tracking-tight text-slate-950 lg:text-[3.15rem]">
                  Profile And Access
                </h1>
                <p className="mt-4 max-w-3xl text-base leading-8 text-slate-600 lg:max-w-2xl">
                  One shared identity stays portable across salon locations. Ellyanna Boothshare access is based on your Ellyanna Booking role, while profile details stay consistent when you switch salon locations.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <HeroPill tone="solid">{accountStateLabel}</HeroPill>
                  <HeroPill>Shared account</HeroPill>
                  <HeroPill>{heroContextLabel}</HeroPill>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  {token ? (
                    <>
                      {canViewMarketplaceBookings ? (
                        <Link
                          to="/account/rentals"
                          className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#2f3566] hover:bg-slate-100"
                        >
                          View My Rentals
                        </Link>
                      ) : null}
                      <Link
                        to="/search"
                        className="rounded-full border border-[#2f3566]/20 bg-white px-5 py-2.5 text-sm font-semibold text-[#2f3566] hover:bg-[#2f3566]/5"
                      >
                        Search Workspaces
                      </Link>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#2f3566] hover:bg-slate-100"
                    >
                      Sign In
                    </button>
                  )}
                </div>
              </div>

              <div className="rounded-[2rem] border border-slate-200 bg-white/80 px-6 py-6 shadow-[0_14px_45px_rgba(47,53,102,0.08)]">
                <div className="text-xs font-semibold uppercase tracking-[0.24em] text-rose-500">
                  Account Snapshot
                </div>
                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <SnapshotItem
                    label="Current salon"
                    value={currentSalonProfile.current_salon_id ? currentSalonProfile.current_salon_id : "None"}
                    detail={currentSalonProfile.current_salon_name || "No salon selected"}
                  />
                  <SnapshotItem
                    label="Role"
                    value={currentRoleLabel}
                    detail={user?.full_name || "Shared account"}
                  />
                  <SnapshotItem
                    label="Ellyanna Boothshare access"
                    value={currentRoleLabel}
                    detail={ownershipModeLabel}
                  />
                  <SnapshotItem
                    label="Access"
                    value={`${ownerSalonCount + technicianSalonCount}`}
                    detail={`${ownerSalonCount} owner / ${technicianSalonCount} technician`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 px-7 py-7 lg:grid-cols-[0.95fr,1.05fr] lg:px-9">
            <div className="space-y-5">
              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Salon linkage
                </div>
                <div className="mt-4 grid gap-5 md:grid-cols-2">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Linked salon IDs
                    </div>
                    <BadgeList
                      values={linkedSalonIds}
                      emptyMessage="No linked salons found."
                    />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Linked business IDs
                    </div>
                    <BadgeList
                      values={linkedBusinessIds}
                      emptyMessage="No linked businesses found."
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Operator affiliations
                </div>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  These records keep one portable operator identity linked to many salon locations and workspace arrangements without creating a separate salon identity for every location.
                </p>
                {operatorAffiliations.length ? (
                  <div className="mt-4 space-y-3">
                    {operatorAffiliations.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4"
                      >
                        <div className="text-sm font-semibold text-slate-900">
                          {item.host_salon_name || `Salon #${item.host_salon_id}`}
                        </div>
                        <div className="mt-1 text-sm text-slate-600">
                          {item.workspace_name || `Workspace #${item.workspace_id || "n/a"}`} •{" "}
                          {titleCaseComplianceLabel(item.affiliation_kind)} •{" "}
                          {titleCaseComplianceLabel(item.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-slate-600">
                    No operator affiliations have been recorded yet.
                  </p>
                )}
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Operating boundary
                </div>
                <div className="mt-3 text-xl font-semibold text-slate-900">
                  {operationalBoundary.relationshipLabel}
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {operationalBoundary.relationshipDescription}
                </p>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-slate-500">
                      Workspace provider controls
                    </div>
                    <div className="mt-2 space-y-2 text-sm text-slate-700">
                      {operationalBoundary.hostControls?.length ? (
                        operationalBoundary.hostControls.map((item) => <p key={item}>{item}</p>)
                      ) : (
                        <p>Only workspace publication, agreement, and payment workflow controls remain with the workspace provider.</p>
                      )}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-slate-500">
                      Renter controls
                    </div>
                    <div className="mt-2 space-y-2 text-sm text-slate-700">
                      {operationalBoundary.renterControls?.length ? (
                        operationalBoundary.renterControls.map((item) => <p key={item}>{item}</p>)
                      ) : (
                        <p>No renter-controlled commercial settings are recorded yet for this default path.</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                  <p>
                    <span className="font-semibold text-slate-900">Client ownership:</span>{" "}
                    {operationalBoundary.crmScopeLabel}
                  </p>
                  <p className="mt-2">
                    <span className="font-semibold text-slate-900">Reporting scope:</span>{" "}
                    {operationalBoundary.reportingScopeLabel}
                  </p>
                  <p className="mt-2">
                    <span className="font-semibold text-slate-900">Scheduling scope:</span>{" "}
                    {operationalBoundary.schedulingScopeLabel}
                  </p>
                </div>
                      {operationalBoundary.independentRenter ? (
                  <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-sky-700">
                    Ellyanna Boothshare access is based on the Ellyanna Booking role for this account.
                    {marketplaceProfile?.has_technician_access
                      ? " This account still has separate salon technician access, but that should remain distinct from the independent rental path."
                      : ""}
                  </div>
                ) : null}
              </div>

              <AccessList
                title="Owner access"
                rows={marketplaceProfile?.owner_salons || []}
                emptyMessage="This account does not own an Ellyanna salon yet."
              />
              <AccessList
                title="Technician access"
                rows={marketplaceProfile?.technician_salons || []}
                emptyMessage="This account is not linked to a salon staff profile yet."
              />
            </div>

            <div className="space-y-6">
              {!token ? (
                <div className="rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-600">
                  Sign in to manage your marketplace identity, current salon profile,
                  and owner upgrade path.
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600"
                    >
                      Sign In
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <form
                    onSubmit={handleSaveMarketplaceIdentity}
                    className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm"
                  >
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                        Marketplace identity
                      </div>
                      <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                        Shared Ellyanna Boothshare profile
                      </h2>
                    </div>
                    <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600">
                      These details belong to your shared independent-operator identity and
                      should not change when you switch salon locations.
                    </p>
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                      <p>
                        <span className="font-semibold text-slate-900">Rental scope:</span>{" "}
                        {operationalBoundary.bookingsScopeLabel}
                      </p>
                      <p className="mt-2">
                        <span className="font-semibold text-slate-900">Agreement scope:</span>{" "}
                        {operationalBoundary.agreementScopeLabel}
                      </p>
                    </div>

                    <div className="mt-6 grid gap-3 md:grid-cols-3">
                      <div className="rounded-[1.3rem] border border-slate-200 bg-slate-50 px-4 py-4">
                        <div className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-slate-500">
                          Login path
                        </div>
                        <div className="mt-2 text-base font-semibold text-slate-950">
                          Email or username
                        </div>
                        <div className="mt-1 text-sm text-slate-500">
                          Your shared Ellyanna login works on both Ellyanna Boothshare and Ellyanna.
                        </div>
                      </div>
                      <div className="rounded-[1.3rem] border border-slate-200 bg-slate-50 px-4 py-4">
                        <div className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-slate-500">
                          Identity scope
                        </div>
                        <div className="mt-2 text-base font-semibold text-slate-950">
                          Shared across salons
                        </div>
                        <div className="mt-1 text-sm text-slate-500">
                          These fields stay the same when you switch salon context.
                        </div>
                      </div>
                      <div className="rounded-[1.3rem] border border-slate-200 bg-slate-50 px-4 py-4">
                        <div className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-slate-500">
                          Editing effect
                        </div>
                        <div className="mt-2 text-base font-semibold text-slate-950">
                          Marketplace identity only
                        </div>
                        <div className="mt-1 text-sm text-slate-500">
                          Salon-specific photo, phone, and display name are edited below.
                        </div>
                      </div>
                    </div>

                    {marketplaceLoading ? (
                      <p className="mt-5 text-sm text-slate-600">Loading marketplace identity...</p>
                    ) : null}
                    {marketplaceError ? (
                      <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {marketplaceError}
                      </div>
                    ) : null}
                    {marketplaceSuccess ? (
                      <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                        {marketplaceSuccess}
                      </div>
                    ) : null}

                    <div className="mt-6 grid gap-4 md:grid-cols-2">
                      <label className="space-y-2">
                        <span className="text-sm font-semibold text-slate-700">
                          Login username
                        </span>
                        <input
                          value={currentSalonProfile.username}
                          onChange={(event) =>
                            setCurrentSalonProfile((prev) => ({
                              ...prev,
                              username: event.target.value,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                          placeholder="username"
                        />
                        <p className="text-xs text-slate-500">
                          Optional. You can sign in with your email or this username.
                        </p>
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-semibold text-slate-700">Display name</span>
                        <input
                          value={marketplaceForm.display_name}
                          onChange={(event) =>
                            setMarketplaceForm((prev) => ({
                              ...prev,
                              display_name: event.target.value,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-semibold text-slate-700">
                          Salon or independent professional business name
                        </span>
                        <input
                          value={marketplaceForm.business_name}
                          onChange={(event) =>
                            setMarketplaceForm((prev) => ({
                              ...prev,
                              business_name: event.target.value,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-semibold text-slate-700">Marketplace phone</span>
                        <input
                          value={marketplaceForm.phone}
                          onChange={(event) =>
                            setMarketplaceForm((prev) => ({
                              ...prev,
                              phone: event.target.value,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-semibold text-slate-700">License type</span>
                        <input
                          value={marketplaceForm.license_type}
                          onChange={(event) =>
                            setMarketplaceForm((prev) => ({
                              ...prev,
                              license_type: event.target.value,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-semibold text-slate-700">Instagram</span>
                        <input
                          value={marketplaceForm.instagram_handle}
                          onChange={(event) =>
                            setMarketplaceForm((prev) => ({
                              ...prev,
                              instagram_handle: event.target.value,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-semibold text-slate-700">Emergency contact</span>
                        <input
                          value={marketplaceForm.emergency_contact}
                          onChange={(event) =>
                            setMarketplaceForm((prev) => ({
                              ...prev,
                              emergency_contact: event.target.value,
                            }))
                          }
                          className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                        />
                      </label>
                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-semibold text-slate-700">Bio</span>
                        <textarea
                          rows={4}
                          value={marketplaceForm.bio}
                          onChange={(event) =>
                            setMarketplaceForm((prev) => ({
                              ...prev,
                              bio: event.target.value,
                            }))
                          }
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900"
                        />
                      </label>
                    </div>

                    <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                      <div className="text-sm font-semibold text-slate-900">
                        Ellyanna Boothshare access
                      </div>
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-700">
                        <div className="font-semibold text-slate-900">Based on Ellyanna Booking role</div>
                        <p className="mt-2 leading-6 text-slate-600">
                          Owners, managers, receptionists, independent professionals, and super admins can use Ellyanna Boothshare account tools. Technician and apprentice workflows stay in Ellyanna Booking.
                        </p>
                      </div>
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                        Owner-of-record client posture:{" "}
                        <span className="font-semibold text-slate-900">
                          {ownershipTitle(defaultOwnershipMode)}
                        </span>
                      </div>
                      <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
                        Workspace providers only get restricted operational visibility for service recovery, warranty, complaints, and follow-up tied to work performed at that location.
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={marketplaceSaving || marketplaceLoading}
                      className="mt-6 rounded-full bg-rose-500 px-5 py-3 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {marketplaceSaving ? "Saving..." : "Save Marketplace Identity"}
                    </button>
                  </form>

                  <form
                    onSubmit={handleSaveCurrentSalonProfile}
                    className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm"
                  >
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Current salon profile
                      </div>
                      <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                        Selected salon details
                      </h2>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      These details come from the existing Ellyanna current-salon profile
                      path and change when you switch salon context in Ellyanna Boothshare.
                    </p>

                    {currentSalonLoading ? (
                      <p className="mt-5 text-sm text-slate-600">Loading current salon profile...</p>
                    ) : null}
                    {currentSalonError ? (
                      <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {currentSalonError}
                      </div>
                    ) : null}
                    {currentSalonSuccess ? (
                      <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                        {currentSalonSuccess}
                      </div>
                    ) : null}

                    <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                      <div className="grid gap-4 md:grid-cols-3">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Staff profile
                          </div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">
                            {currentSalonProfile.has_current_salon_profile
                              ? "Linked to current salon"
                              : "Not linked yet"}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {currentSalonProfile.staff_id
                              ? `Staff ID ${currentSalonProfile.staff_id}`
                              : "No salon-specific staff record available."}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Current business
                          </div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">
                            {currentSalonProfile.current_business_name || "Not available"}
                            {currentSalonProfile.current_business_id
                              ? ` (ID ${currentSalonProfile.current_business_id})`
                              : ""}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Shared email
                          </div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">
                            {user?.email || "Not available"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {currentSalonProfile.has_current_salon_profile ? (
                      <div className="mt-6 space-y-4">
                        <ImageUploadField
                          label="Profile Picture"
                          helper="Shown in salon-specific staff views, including the Ellyanna Boothshare salon switcher."
                          recommended="Square image recommended"
                          value={currentSalonProfile.photo_url}
                          onChange={(next) =>
                            setCurrentSalonProfile((prev) => ({
                              ...prev,
                              photo_url: next || "",
                            }))
                          }
                          onUpload={handleUploadSalonPhoto}
                          onRemove={handleRemoveSalonPhoto}
                          allowUrlPaste={false}
                          maxSizeMb={2}
                        />

                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="space-y-2">
                            <span className="text-sm font-semibold text-slate-700">
                              Current salon display name
                            </span>
                            <input
                              value={currentSalonProfile.salon_display_name}
                              onChange={(event) =>
                                setCurrentSalonProfile((prev) => ({
                                  ...prev,
                                  salon_display_name: event.target.value,
                                }))
                              }
                              className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                              placeholder="Name shown in this salon"
                            />
                          </label>
                          <label className="space-y-2">
                            <span className="text-sm font-semibold text-slate-700">
                              Current salon phone
                            </span>
                            <input
                              value={
                                isValidNANP(currentSalonProfile.phone)
                                  ? formatNANP(currentSalonProfile.phone)
                                  : currentSalonProfile.phone
                              }
                              onChange={(event) =>
                                setCurrentSalonProfile((prev) => ({
                                  ...prev,
                                  phone: toNANPDigits10(event.target.value),
                                }))
                              }
                              className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                              placeholder="(555) 123-4567"
                            />
                            {salonPhoneError ? (
                              <p className="text-xs text-rose-600">{salonPhoneError}</p>
                            ) : null}
                          </label>
                        </div>

                        <button
                          type="submit"
                          disabled={currentSalonSaving || currentSalonLoading}
                          className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {currentSalonSaving ? "Saving..." : "Save Current Salon Profile"}
                        </button>
                      </div>
                    ) : (
                      <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                        {currentSalonProfile.current_salon_id
                          ? "This login does not currently have a staff profile in the selected salon, so salon-specific photo, display name, and phone fields are unavailable."
                          : "Select a salon first if you want to manage current-salon profile details."}
                      </div>
                    )}
                  </form>

                  <form
                    id="owner-upgrade"
                    onSubmit={handleUpgrade}
                    className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm"
                  >
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Ellyanna upgrade path
                    </div>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                      Create your own Ellyanna business setup
                    </h2>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      Upgrade this same shared account when you need your own Ellyanna business, billing, and management setup while keeping Ellyanna Boothshare available for independent workspace relationships.
                    </p>
                    {marketplaceProfile?.has_owner_access ? (
                      <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-700">
                        This account already has owner access in Ellyanna.
                        <div className="mt-3">
                          <a
                            href={runtimeBrand.opsHomeUrl}
                            className="inline-flex rounded-full bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-700"
                          >
                            Open Ellyanna Dashboard
                          </a>
                        </div>
                      </div>
                    ) : (
                      <>
                        {upgradeError ? (
                          <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {upgradeError}
                          </div>
                        ) : null}
                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                          <label className="space-y-2 md:col-span-2">
                            <span className="text-sm font-semibold text-slate-700">
                          Salon or independent professional business name
                            </span>
                            <input
                              value={upgradeForm.salon_name}
                              onChange={(event) =>
                                setUpgradeForm((prev) => ({
                                  ...prev,
                                  salon_name: event.target.value,
                                }))
                              }
                              className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                              required
                            />
                          </label>
                          <label className="space-y-2">
                            <span className="text-sm font-semibold text-slate-700">City</span>
                            <input
                              value={upgradeForm.city}
                              onChange={(event) =>
                                setUpgradeForm((prev) => ({ ...prev, city: event.target.value }))
                              }
                              className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                            />
                          </label>
                          <label className="space-y-2">
                            <span className="text-sm font-semibold text-slate-700">Province</span>
                            <input
                              value={upgradeForm.province}
                              onChange={(event) =>
                                setUpgradeForm((prev) => ({
                                  ...prev,
                                  province: event.target.value,
                                }))
                              }
                              className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                            />
                          </label>
                        </div>
                        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                          Upgrading creates your Ellyanna salon or independent professional
                          setup and redirects you into the normal billing and setup path.
                          Your marketplace history stays on the same account.
                        </div>
                        <button
                          type="submit"
                          disabled={upgrading}
                          className="mt-6 rounded-full bg-[#2f3566] px-5 py-3 text-sm font-semibold text-white hover:bg-[#252a53] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {upgrading
                            ? "Preparing setup..."
                            : "Start Salon / Independent Professionals Setup"}
                        </button>
                      </>
                    )}
                  </form>
                </>
              )}
            </div>
          </div>
        </article>
      </section>
    </MarketplaceShell>
  );
}
